import type { ChatListPage } from './chats';
import { parseConversationReadState } from './chat-read-state';

const DATABASE = 'symphony-chats-v1';
const STORE = 'first-pages';
const MAX_BYTES = 150_000;
const MAX_AGE_MS = 24 * 60 * 60_000;
interface Snapshot { schema: 2; owner: string; channel: string; savedAt: number; page: ChatListPage }
let database: Promise<IDBDatabase | null> | undefined;
let epoch = 0;
const CHANNELS = new Set(['', 'WHATSAPP', 'SMS', 'EXTERNAL_AGENT']);

function open(): Promise<IDBDatabase | null> {
  if (typeof indexedDB === 'undefined') return Promise.resolve(null);
  database ??= new Promise((resolve) => {
    const request = indexedDB.open(DATABASE, 1);
    request.onupgradeneeded = () => request.result.createObjectStore(STORE);
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => { database = undefined; resolve(null); };
    request.onblocked = () => resolve(null);
  });
  return database;
}

/** Async, bounded metadata-only storage. Denied/corrupt storage behaves like an ordinary cache miss. */
export async function readChatsCache(owner: string, channel: string): Promise<Snapshot | null> {
  try {
    const db = await open();
    if (!db || !owner || !CHANNELS.has(channel)) return null;
    return await new Promise((resolve) => {
      const request = db.transaction(STORE).objectStore(STORE).get(channel);
      request.onerror = () => resolve(null);
      request.onsuccess = () => {
        const value = request.result as Snapshot | undefined;
        const age = value ? Date.now() - value.savedAt : Infinity;
        const valid = value?.schema === 2 && value.owner === owner && age >= 0 && age <= MAX_AGE_MS
          && Array.isArray(value.page?.conversations) && value.page.conversations.length <= 40
          && value.page.conversations.every((row) => typeof row?.id === 'string' && Boolean(row.readState))
          && typeof value.page.asOf === 'string'
          && new TextEncoder().encode(JSON.stringify(value)).byteLength <= MAX_BYTES;
        // A saved cursor belongs to an old server snapshot. The fresh first page supplies a current one.
        try {
          resolve(valid ? { ...value, page: { ...value.page, nextCursor: null,
            conversations: value.page.conversations.map((row) => ({ ...row, readState: parseConversationReadState(row.readState) })) } } : null);
        } catch { resolve(null); }
      };
    });
  } catch { return null; }
}

export async function writeChatsCache(owner: string, channel: string, page: ChatListPage): Promise<void> {
  const started = epoch;
  try {
    const snapshot: Snapshot = { schema: 2, owner, channel, page, savedAt: Date.now() };
    if (!owner || !CHANNELS.has(channel) || page.conversations.length > 40
      || new TextEncoder().encode(JSON.stringify(snapshot)).byteLength > MAX_BYTES) return;
    const db = await open();
    if (!db || started !== epoch) return;
    await new Promise<void>((resolve) => {
      const transaction = db.transaction(STORE, 'readwrite');
      transaction.objectStore(STORE).put(snapshot, channel);
      transaction.oncomplete = () => resolve();
      transaction.onerror = () => resolve();
      transaction.onabort = () => resolve();
    });
  } catch { /* Storage is optional. */ }
}

export async function clearChatsCache(): Promise<void> {
  epoch += 1;
  try {
    const db = await open();
    if (!db) return;
    await new Promise<void>((resolve) => {
      const transaction = db.transaction(STORE, 'readwrite');
      transaction.objectStore(STORE).clear();
      transaction.oncomplete = () => resolve();
      transaction.onerror = () => resolve();
      transaction.onabort = () => resolve();
    });
  } catch { /* Storage is optional. */ }
}

const scrollPositions = new Map<string, number>();
export function chatsScrollKey(owner: string, query: string, channel: string): string {
  return JSON.stringify([owner, query.trim(), channel]);
}
export function savedChatsScroll(key: string): number { return scrollPositions.get(key) ?? 0; }
export function saveChatsScroll(key: string, top: number): void {
  scrollPositions.delete(key);
  scrollPositions.set(key, Math.max(0, top));
  while (scrollPositions.size > 32) scrollPositions.delete(scrollPositions.keys().next().value!);
}
